import { useEffect, useState } from "react";
import { ListTodo } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_COLORS = {
  "NOT STARTED": "bg-sand text-mutedink",
  "IN PROGRESS": "bg-teal-light text-teal",
  "CLIENT ACTION NEEDED": "bg-champagne-light text-champagne-dark",
  "FULL CIRCLE ACTION NEEDED": "bg-teal-light text-teal",
  "THIRD PARTY PENDING": "bg-sand text-mutedink",
  "GOVERNMENT PENDING": "bg-sand text-mutedink",
  COMPLETED: "bg-sage-light text-sage",
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([api.get("/tasks"), api.get("/meta/options")])
      .then(([t, m]) => { setTasks(t.data); setStatuses(m.data.task_statuses); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (task, status) => {
    try {
      await api.patch(`/tasks/${task.task_id}`, { status });
      toast.success("Task updated.");
      load();
    } catch {
      toast.error("Could not update the task.");
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  if (loading) {
    return <div className="py-16 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-teal border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="portal-tasks-page">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight">Tasks</h1>
        <p className="mt-1 text-sm text-mutedink">Action items assigned by your Full Circle team. Update the status as you work on them.</p>
      </div>

      {tasks.length === 0 ? (
        <div className="bg-surface border border-dashed border-line rounded-2xl p-10 text-center">
          <ListTodo className="h-10 w-10 text-line mx-auto mb-4" />
          <p className="text-sm text-mutedink">No tasks right now. When your team needs something from you, it will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((t) => {
            const overdue = t.due_date && t.due_date < today && t.status !== "COMPLETED";
            return (
              <div key={t.task_id} className="bg-surface border border-line rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`task-row-${t.task_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-navy">{t.title}</p>
                  <p className="text-xs text-mutedink mt-1">
                    {t.due_date && <>Due {t.due_date} {overdue && <span className="text-red-600 font-semibold">· Overdue</span>} · </>}
                    Assigned by {t.created_by}
                  </p>
                  {t.notes && <p className="text-xs text-mutedink mt-1">{t.notes}</p>}
                </div>
                <Select value={t.status} onValueChange={(v) => setStatus(t, v)}>
                  <SelectTrigger className={`w-full sm:w-56 text-xs font-semibold border-0 ${STATUS_COLORS[t.status]}`} data-testid={`task-status-${t.task_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
